import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { withTranslation } from 'react-i18next';
import SearchResults from '../Components/Search/SearchResults';
import routes from '../Constants/routes';
import customerRoutes from '../Constants/Customer/customerRoutes';
import searchActions from '../Actions/searchActions';
import SearchHistory from '../Components/Search/SearchHistory';
import useModal from "../Hooks/useModal";
import Modals from "../Constants/modals";
import ModalManager from "../Components/ModalManagers/ModalManager";
import CustomerModal from "../Components/Customer/CustomerComponents/CustomerModal";
import { CardContainer } from "../Components/Card/Card";
import DashboardContainer from "./DashboardContainer";
import Tabs from "../Components/Tabs/Tabs";
import { useTabs } from "../Hooks/Tabs/useTabs";

const SearchParams = {
    Input: 'input',
    Email: 'email',
}

const SEARCH_CONFIG = {
    [SearchParams.Input]: {
        action: 'searchCustomers',
        placeholderKey: 'searchcontainer.searchByCustomer',
        labelKey: 'searchcontainer.search',
    },
    [SearchParams.Email]: {
        action: 'searchByContactPerson',
        placeholderKey: 'searchcontainer.searchByContact',
        labelKey: 'searchcontainer.byContact',
    },
};

function SearchContainer({ dropdownlists, t, searchState, searchActions, user, isSearching }) {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const { openModal } = useModal();
    const [searchText, setSearchText] = useState("");
    const isFirstRender = useRef(true);

    const getFirstSearchParam = () => {
        const entries = Array.from(searchParams.entries());
        const [key, value] = entries[0] || [null, null];
        return { key, value };
    };

    const tabs = [
        {
            label: t(SEARCH_CONFIG[SearchParams.Input].labelKey),
            id: SearchParams.Input,
        },
        {
            label: t(SEARCH_CONFIG[SearchParams.Email].labelKey),
            id: SearchParams.Email,
        }
    ];

    const getInitialTab = () => {
        const { key } = getFirstSearchParam();
        return tabs.find(tab => tab.id === key) || tabs[0];
    };

    const { activeTab, setActiveTab } = useTabs(getInitialTab());

    // Handle search based on URL params
    useEffect(() => {
        const { key, value } = getFirstSearchParam();

        if (value) {
            setSearchText(value);
            const config = SEARCH_CONFIG[key];
            if (config) {
                searchActions[config.action](value);
            }
        } else if (searchState.searchResult !== null) {
            setSearchText(searchState.searchInput);
        } else {
            setSearchText("");
            searchActions.emptySearchResult();
        }
    }, [searchParams]);

    // Auto-navigate when single result
    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        if (searchState.searchResult?.length === 1) {
            navigateToCustomer(searchState.searchResult[0].pathId);
        }
    }, [searchState?.searchResult?.length]);

    const handleSearch = () => {
        if (searchText) {
            const searchParam = activeTab?.id || SearchParams.Input;
            setSearchParams({ [searchParam]: searchText });
        } else {
            navigate(routes.search);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    const navigateToCustomer = (customerNumber) => {
        navigate(customerRoutes.customer(customerNumber));
    };

    const handleTabChange = (clickedTab) => {
        if (clickedTab.id !== activeTab?.id) {
            setSearchParams({});
            setSearchText("");
            searchActions.emptySearchResult();
        }
    };

    const placeholder = t(SEARCH_CONFIG[activeTab?.id]?.placeholderKey || SEARCH_CONFIG[SearchParams.Input].placeholderKey);

    // Reusable centered grid column component
    const GridColumn = ({ children }) => (
        <div className="d-grid grid-cols-6 gap-16">
            <div className="grid-col-start-3 grid-col-span-2">
                {children}
            </div>
        </div>
    );

    const CreateCustomerButton = () => (
        <div className="pt-24 float-right">
            <button className="btn" onClick={() => openModal(Modals.CreateCustomer)}>
                {t('searchcontainer.createcustomer')}
            </button>
        </div>
    );

    const Search = () => (
        <div className="form-group search-group">
            <input
                type="text"
                className="form-control"
                placeholder={placeholder}
                onKeyDown={handleKeyDown}
                onChange={(e) => setSearchText(e.target.value)}
                value={searchText}
                autoFocus
            />
            <span className="search-icon cursorpointer" onClick={handleSearch}></span>
            <CreateCustomerButton />
        </div>
    );

    return (
        <CardContainer>
            <GridColumn>
                <Tabs
                    tabs={tabs}
                    activeTab={activeTab}
                    onTabChange={handleTabChange}
                    setActiveTab={setActiveTab}
                />
            </GridColumn>
            <GridColumn>
                <Search />
            </GridColumn>
            <SearchResults
                state={searchState}
                isSearching={isSearching}
                dropdownlists={dropdownlists}
                clickEvent={navigateToCustomer}
                user={user}
            />
            <SearchHistory clickEvent={navigateToCustomer}/>
            <ModalManager modals={{ [Modals.CreateCustomer]: CustomerModal }}/>
            <DashboardContainer />
        </CardContainer>
    );
}

function mapStateToProps(state) {
    return {
        searchState: state.SearchReducer,
        dropdownlists: state.CommonReducer.dropdownlists,
        user: state.CommonReducer.user,
        isSearching: state.CommonReducer.dataLoading.search
    };
}

function mapDispatchToProps(dispatch) {
    return {
        searchActions: bindActionCreators(searchActions, dispatch),
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(SearchContainer));
